<template>
  <div v-if="isVisible">
    <div v-if="showTitle" class="field__inner-title mb-3">
      <div>{{ schema.title }}</div>
      <p v-if="schema.help">{{ schema.help || '' }}</p>
    </div>

    <div class="row">
      <div class="col-md-6">
        <v-menu
          ref="datePicker"
          :close-on-content-click="false"
          v-model="datePickerModal"
          :nudge-right="40"
          transition="scale-transition"
          min-width="290px"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <div v-on="!isDisabled && on" style="position: relative">
              <v-text-field
                slot="activator"
                v-model="dateInputValue"
                :readonly="!usesUsDateMask"
                :label="fieldLabel"
                :placeholder="datePlaceholder"
                :class="fieldClasses"
                :rules="rules"
                hint=""
                persistent-hint
                v-mask="dateMask"
                :inputmode="
                  usesUsDateMask ? formatInputProps.inputmode : undefined
                "
                :prepend-inner-icon="getInputIcon('today')"
                :disabled="isDisabled"
                clearable
                @focus="onDateFocus"
                @blur="onDateBlur"
                @keydown.enter.native="onDateEnter"
                @keydown.esc.native="onDateEscape"
              />
            </div>
          </template>
          <v-date-picker
            v-model="localDate"
            @input="handleDateTimeChange('date')"
            color="primary"
            no-title
            :min="schema.min"
            :max="schema.max"
          />
        </v-menu>
      </div>

      <div class="col-md-6">
        <v-menu
          ref="timePicker"
          :close-on-content-click="false"
          v-model="timePickerModal"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          max-width="290px"
          min-width="290px"
        >
          <template v-slot:activator="{ on }">
            <div v-on="!isDisabled && on">
              <v-text-field
                slot="activator"
                :class="fieldClasses"
                v-model="startTime"
                @input="handleDateTimeChange('time')"
                label="Time"
                hint=""
                persistent-hint
                readonly
                :prepend-inner-icon="getInputIcon('access_time')"
                :disabled="isDisabled"
                clearable
              />
            </div>
          </template>
          <v-time-picker
            v-if="timePickerModal"
            v-model="localTime"
            @input="handleDateTimeChange('time')"
            color="appMain"
            :allowed-minutes="allowedMinutes"
            ampm-in-title
            @click:minute="timePickerModal = false"
          />
        </v-menu>
      </div>
    </div>
  </div>
</template>

<script>
import GenericField from './GenericField';
import {
  format,
  formatISO,
  parse,
  roundToNearestMinutes,
  isValid,
} from 'date-fns';
import { convertTimeToAmPm } from '@/filters/helpers';
import { displayFormat, validateFormat } from '@/lib/fieldFormats';
import { safeMask } from '@/lib/safeMaskDirective';

const US_DATE_MDY = 'us_date_mdy';

export default {
  name: 'DateTimeField',

  mixins: [GenericField],
  directives: { mask: safeMask },

  data() {
    return {
      datePickerModal: false,
      timePickerModal: false,
      localDate: '',
      localTime: '',
      dateDraft: null,
    };
  },

  computed: {
    startTime: {
      get() {
        return convertTimeToAmPm(this.localTime);
      },
      set(value) {
        this.localTime = value;
      },
    },

    fieldLabel() {
      const label = this.schema?.label;

      return label && !this.showTitle ? label : 'Date';
    },

    usesUsDateMask() {
      return this.resolvedFormat === US_DATE_MDY;
    },

    datePlaceholder() {
      if (!this.usesUsDateMask) return '';
      return this.formatInputProps.placeholder || 'MM/DD/YYYY';
    },

    dateMask() {
      if (!this.usesUsDateMask) return '';
      return this.formatInputProps.mask || '##/##/####';
    },

    dateInputValue: {
      get() {
        if (this.usesUsDateMask && this.dateDraft != null) {
          return this.dateDraft;
        }
        if (!this.localDate) return '';
        if (!this.usesUsDateMask) return this.localDate;
        return displayFormat(US_DATE_MDY, this.localDate, { context: 'edit' });
      },
      set(value) {
        if (!this.usesUsDateMask) {
          if (value) return;
          this.localDate = '';
          this.handleDateTimeChange('date');
          return;
        }

        this.datePickerModal = false;
        const next = value == null ? '' : String(value);
        const saved = this.localDate
          ? displayFormat(US_DATE_MDY, this.localDate, { context: 'edit' })
          : '';
        this.dateDraft = next === saved ? null : next;
      },
    },
  },

  watch: {
    value: 'syncDateTime',
  },

  methods: {
    allowedMinutes(v) {
      if (typeof this.schema?.allowedMinutes === 'function') {
        return this.schema.allowedMinutes(v);
      }
      return v % 15 === 0;
    },

    setWallTime(value) {
      const [date, time = '00:00'] = String(value).split(' ');
      this.localDate = date;
      this.localTime = time.slice(0, 5);
    },

    syncDateTime(value) {
      this.dateDraft = null;

      if (!value) {
        this.localDate = '';
        this.localTime = '';
        return;
      }

      if (this.schema.wallTime) {
        this.setWallTime(value);
        return;
      }

      const date = this.parseIncomingValue(value);

      if (isValid(date)) {
        this.setLocalDateTime(date);
      }
    },

    parseIncomingValue(value) {
      if (typeof value === 'number') {
        return new Date(value * 1000);
      }

      return new Date(value);
    },

    setLocalDateTime(date) {
      this.localDate = format(date, 'yyyy-MM-dd');
      this.localTime = format(date, 'HH:mm');
    },

    resetDateTime() {
      this.localDate = '';
      this.localTime = '';
      this.localValue = '';
    },

    getInputIcon(icon) {
      return this.schema?.hideLabel ? icon : '';
    },

    parseLocalDateTime() {
      return parse(
        `${this.localDate} ${this.localTime}`,
        'yyyy-MM-dd HH:mm',
        new Date()
      );
    },

    updateLocalValue() {
      if (!this.localDate) {
        this.resetDateTime();
        return;
      }

      if (!this.localTime) {
        this.localTime = '00:00';
      }

      const date = this.parseLocalDateTime();

      if (!isValid(date)) return;

      this.localValue = this.schema.wallTime
        ? `${this.localDate} ${this.localTime}`
        : formatISO(date);
    },

    onDateFocus() {
      // Focus alone must not emit or clear the stored value.
    },

    onDateBlur() {
      if (!this.usesUsDateMask || this.dateDraft == null) return;

      const draft = this.dateDraft;
      if (draft === '') {
        this.dateDraft = null;
        this.localDate = '';
        this.handleDateTimeChange('date');
        return;
      }

      const parsed = validateFormat(US_DATE_MDY, draft, false);
      if (!parsed.valid) return;

      this.dateDraft = null;
      if (parsed.value === this.localDate) return;
      this.localDate = parsed.value;
      this.handleDateTimeChange('date');
    },

    onDateEnter(event) {
      if (!this.usesUsDateMask) return;
      if (event && event.preventDefault) event.preventDefault();
      this.onDateBlur();
    },

    onDateEscape() {
      if (!this.usesUsDateMask) return;
      this.dateDraft = null;
      this.datePickerModal = false;
    },

    handleDateTimeChange(type) {
      if (type === 'date') {
        this.dateDraft = null;
        this.datePickerModal = false;
      }

      this.updateLocalValue();
    },

    initializeDateTime() {
      if (!this.localValue && this.schema.noDefault) return;

      if (this.schema.wallTime && this.localValue) {
        this.setWallTime(this.localValue);
        return;
      }

      let date = roundToNearestMinutes(new Date(), {
        nearestTo: 15,
      });

      if (this.localValue) {
        date = this.parseIncomingValue(
          this.isStringDateMode() ? this.localValue : Number(this.localValue)
        );
      }

      if (!isValid(date)) return;

      this.setLocalDateTime(date);
      this.updateLocalValue();
    },

    isStringDateMode() {
      return typeof this.localValue === 'string' && !this.schema.timestamp;
    },
  },

  mounted() {
    this.initializeDateTime();
  },
};
</script>
